import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dropsService, jobsService } from '../services/api';
import { Modal } from '../components/Modal';
import { AreaDrawBar } from '../components/AreaDrawBar';
import { loadGoogleMaps } from '../utils/googleMaps';
import { startAreaDraw } from '../utils/mapAreaDraw';
import { formatMoney } from '../utils/pricing';
import './Drops.css';

const TARGETS = { min: 20, max: 300, step: 10 };
const fmtDate = (v) => (v ? new Date(String(v).includes('T') ? v : `${String(v).replace(' ', 'T')}Z`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

/**
 * Letterbox drops (backlog #691) — one row per drop: where it was centred,
 * how many letterboxes, how many driveways are traced and how the walk went.
 * "New drop" picks the job (or an address), sets roughly how many letterboxes,
 * and shows the letterboxes the cadastre found before anything is saved —
 * or, when the radius misses a street, takes an outline drawn on the map
 * instead (backlog #694).
 */
export default function Drops() {
  const navigate = useNavigate();
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(false);

  const load = useCallback(async () => {
    const r = await dropsService.getAll();
    if (r.success) setDrops(r.drops || []);
    else setError(r.error || 'Could not load the drops');
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function remove(d) {
    if (!window.confirm(`Delete "${d.name}" and its ${d.property_count} letterboxes? Traced driveways go with it.`)) return;
    const r = await dropsService.delete(d.id);
    if (r.success) load(); else setError(r.error || 'Could not delete the drop');
  }

  return (
    <div className="drops-page">
      <div className="page-header">
        <h1 className="page-title">Letterbox drops</h1>
        <button className="btn btn-primary" onClick={() => setModal(true)}>+ New drop</button>
      </div>
      <p className="text-muted drops-intro">The neighbours around a job: the nearest hundred letterboxes from the Queensland cadastre — or every letterbox inside an area you draw on the map — each driveway traced and priced so the number is ready when they ring, and the walk ticked off house by house.</p>

      {error && <p className="form-error">{error}</p>}
      {loading ? (
        <div className="empty-state"><div className="loading-spinner"></div></div>
      ) : drops.length === 0 ? (
        <div className="empty-state"><p>No drops yet. Start one around a job — the letterboxes come from the cadastre, you trace the driveways.</p></div>
      ) : (
        <div className="drop-list">
          {drops.map((d) => (
            <div key={d.id} className="drop-row">
              <button type="button" className="drop-row-main" onClick={() => navigate(`/drops/${d.id}`)}>
                <h3>{d.name}</h3>
                <p className="text-muted">
                  {d.centre_address || `${Number(d.centre_lat).toFixed(4)}, ${Number(d.centre_lng).toFixed(4)}`}
                  {d.job_number ? ` · ${d.job_number}${d.job_title ? ` ${d.job_title}` : ''}` : ''}
                  {` · ${fmtDate(d.created_at)}`}
                </p>
                <div className="drop-chips">
                  <span className="drop-chip">{d.property_count} letterbox{d.property_count === 1 ? '' : 'es'}{d.area_geojson ? ' · with a drawn area' : ` within ${d.radius_m} m`}</span>
                  <span className={`drop-chip ${d.measured_count ? 'teal' : ''}`}>{d.measured_count} traced</span>
                  <span className={`drop-chip ${d.dropped_count ? 'green' : ''}`}>{d.dropped_count} dropped</span>
                  {d.no_junk_mail_count > 0 && <span className="drop-chip red">{d.no_junk_mail_count} no junk mail</span>}
                  {d.booked_count > 0 && <span className="drop-chip gold">{d.booked_count} booked</span>}
                  {d.estimate_sum > 0 && <span className="drop-chip">{formatMoney(d.estimate_sum)} of driveways</span>}
                </div>
              </button>
              <button type="button" className="btn btn-sm btn-outline drop-row-delete" onClick={() => remove(d)} aria-label={`Delete ${d.name}`}>Delete</button>
            </div>
          ))}
        </div>
      )}

      <NewDropModal isOpen={modal} onClose={() => setModal(false)} onCreated={(d) => { setModal(false); navigate(`/drops/${d.id}`); }} />
    </div>
  );
}

function NewDropModal({ isOpen, onClose, onCreated }) {
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState('');
  const [address, setAddress] = useState('');
  const [target, setTarget] = useState(100);
  const [name, setName] = useState('');
  const [preview, setPreview] = useState(null); // { centre, radius_m, count, properties, polygon? }
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [drawing, setDrawing] = useState(false);
  const [corners, setCorners] = useState(0);
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const drawnRef = useRef([]);
  const drawRef = useRef(null);     // the outline being drawn (mapAreaDraw controller)
  const lookUpRef = useRef(() => {});

  useEffect(() => {
    if (!isOpen) {
      if (drawRef.current) drawRef.current.cancel();
      drawRef.current = null;
      return;
    }
    setJobId(''); setAddress(''); setTarget(100); setName(''); setPreview(null); setBusy(''); setError(''); setDrawing(false); setCorners(0);
    mapRef.current = null; drawnRef.current = []; drawRef.current = null;
    jobsService.getAll().then((r) => { if (r.success) setJobs((r.jobs || []).filter((j) => j.customer_name)); });
  }, [isOpen]);

  const centreBody = useCallback(() => {
    if (preview?.centre) return { lat: preview.centre.lat, lng: preview.centre.lng, address: preview.centre.address, job_id: preview.centre.job_id };
    if (jobId) return { job_id: Number(jobId) };
    if (address.trim()) return { address: address.trim() };
    return null;
  }, [preview, jobId, address]);

  // The letterboxes for the centre: the nearest `n`, or — with a ring drawn on the map — everything inside it.
  async function lookUp(body = centreBody(), n = target, ring = null) {
    if (!body) { setError('Pick a job or type an address first.'); return; }
    setBusy('preview'); setError('');
    const r = await dropsService.preview({ ...body, target: n, ...(ring ? { polygon: ring } : {}) });
    setBusy('');
    if (!r.success) { setError(r.error || 'Could not look up the letterboxes'); return; }
    setPreview({
      centre: r.centre, radius_m: r.radius_m, count: r.count, total_in_radius: r.total_in_radius, total_in_area: r.total_in_area,
      polygon: ring ? (r.ring || ring) : null, properties: r.properties || []
    });
  }
  lookUpRef.current = lookUp;

  // A new centre (job / address) throws the old preview away; the target re-runs it on the same centre.
  function pickJob(v) { setJobId(v); if (v) setAddress(''); setPreview(null); }
  function typeAddress(v) { setAddress(v); if (v) setJobId(''); setPreview(null); }
  useEffect(() => {
    if (!preview || preview.polygon) return undefined;
    const t = setTimeout(() => { if (preview.count !== target) lookUp(centreBody(), target); }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  // The letterboxes on a satellite map: a dot each, the search radius (or the outline), the centre.
  useEffect(() => {
    if (!isOpen || !preview || !mapEl.current) return undefined;
    let stale = false;
    (async () => {
      try {
        const maps = await loadGoogleMaps();
        if (stale || !mapEl.current) return;
        const centre = { lat: preview.centre.lat, lng: preview.centre.lng };
        if (!mapRef.current) {
          mapRef.current = new maps.Map(mapEl.current, { center: centre, zoom: 17, mapTypeId: 'satellite', tilt: 0, disableDefaultUI: true, zoomControl: true, clickableIcons: false });
        }
        for (const o of drawnRef.current) o.setMap(null);
        drawnRef.current = [];
        const map = mapRef.current;
        let fit;
        if (preview.polygon) {
          const path = preview.polygon.map(([lat, lng]) => ({ lat: Number(lat), lng: Number(lng) }));
          drawnRef.current.push(new maps.Polygon({ map, paths: [path], strokeColor: '#FACC15', strokeOpacity: 0.9, strokeWeight: 2, fillColor: '#FACC15', fillOpacity: 0.06, clickable: false, zIndex: 5 }));
          fit = new maps.LatLngBounds();
          path.forEach((ll) => fit.extend(ll));
        } else {
          const circle = new maps.Circle({ map, center: centre, radius: preview.radius_m, strokeColor: '#FACC15', strokeOpacity: 0.8, strokeWeight: 1.5, fillOpacity: 0.04, clickable: false });
          drawnRef.current.push(circle);
          fit = circle.getBounds();
        }
        drawnRef.current.push(new maps.Marker({ map, position: centre, title: preview.centre.address || 'Centre', zIndex: 20 }));
        for (const p of preview.properties) {
          drawnRef.current.push(new maps.Marker({
            map, position: { lat: p.lat, lng: p.lng }, title: p.address, clickable: false,
            icon: { path: maps.SymbolPath.CIRCLE, scale: 5, fillColor: '#2DD4BF', fillOpacity: 0.95, strokeColor: '#0b0f14', strokeWeight: 1 }
          }));
        }
        if (fit) map.fitBounds(fit, 20);
      } catch (e) {
        if (!stale) setError(e.message || 'Could not load the map');
      }
    })();
    return () => { stale = true; };
  }, [isOpen, preview]);

  // Draw the area instead: click around the streets, then every letterbox inside the outline is the drop.
  function startDraw() {
    const map = mapRef.current, maps = window.google?.maps;
    if (!map || !maps || drawRef.current) return;
    setDrawing(true); setCorners(0); setError('');
    drawRef.current = startAreaDraw(maps, map, {
      ignore: drawnRef.current,
      onChange: setCorners,
      onCancel: () => { drawRef.current = null; setDrawing(false); },
      onFinish: (ring) => { drawRef.current = null; setDrawing(false); lookUpRef.current(undefined, undefined, ring); }
    });
  }
  function useNearest() { lookUp(centreBody(), target); }

  async function create() {
    const body = centreBody();
    if (!body || !preview) return;
    setBusy('create'); setError('');
    const r = await dropsService.create({ ...body, target, radius_m: preview.radius_m, ...(preview.polygon ? { polygon: preview.polygon } : {}), name: name.trim() || undefined });
    setBusy('');
    if (!r.success) { setError(r.error || 'Could not create the drop'); return; }
    onCreated(r.drop);
  }

  const job = jobs.find((j) => String(j.id) === String(jobId));
  const suggestedName = preview?.centre?.address ? `Around ${preview.centre.address.split(',')[0]}` : job ? `Around ${job.job_number}` : '';
  const streets = preview ? [...new Set(preview.properties.map((p) => p.street_full).filter(Boolean))] : [];
  const inArea = !!preview?.polygon;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New letterbox drop" size="large">
      <div className="new-drop">
        <div className="new-drop-form">
          <div className="form-group">
            <label htmlFor="nd_job">Around which job?</label>
            <select id="nd_job" value={jobId} onChange={(e) => pickJob(e.target.value)}>
              <option value="">— pick a job —</option>
              {jobs.map((j) => <option key={j.id} value={j.id}>{j.job_number} · {j.title || 'Job'} · {j.customer_name}{j.suburb ? ` (${j.suburb})` : ''}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="nd_address">…or an address</label>
            <input id="nd_address" type="text" placeholder="48 Serafina Drive, Helensvale" value={address} onChange={(e) => typeAddress(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookUp(); } }} />
          </div>
          <div className="form-group">
            <label htmlFor="nd_target">How many letterboxes? <strong>{inArea ? preview.count : target}</strong></label>
            <input id="nd_target" type="range" min={TARGETS.min} max={TARGETS.max} step={TARGETS.step} value={target} onChange={(e) => setTarget(Number(e.target.value))} disabled={inArea || drawing} />
            <p className="text-muted new-drop-hint">
              {inArea
                ? <>Every letterbox inside the outline you drew. <button type="button" className="nd-link" onClick={useNearest}>Use the nearest {target} instead</button></>
                : 'The nearest ones to the job. The search grows outwards until it has this many — or draw the area you want on the map.'}
            </p>
          </div>
          <div className="form-group">
            <label htmlFor="nd_name">Name</label>
            <input id="nd_name" type="text" placeholder={suggestedName || 'Around the job'} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          {!preview && (
            <button type="button" className="btn btn-outline" onClick={() => lookUp()} disabled={busy === 'preview' || (!jobId && !address.trim())}>
              {busy === 'preview' ? 'Looking up…' : 'Find the letterboxes'}
            </button>
          )}
          {error && <p className="form-error">{error}</p>}
        </div>

        <div className="new-drop-preview">
          {preview ? (
            <>
              <p className="new-drop-summary">
                {inArea
                  ? <><strong>{preview.count} letterbox{preview.count === 1 ? '' : 'es'}</strong> inside your outline</>
                  : <><strong>{preview.count} letterbox{preview.count === 1 ? '' : 'es'}</strong> within <strong>{preview.radius_m} m</strong> of {preview.centre.address || 'the centre'}</>}
                {busy === 'preview' && <span className="text-muted"> · updating…</span>}
              </p>
              {streets.length > 0 && <p className="text-muted new-drop-streets">{streets.join(' · ')}</p>}
              <div className="new-drop-map-wrap">
                <div className="new-drop-map" ref={mapEl} />
                {drawing && <AreaDrawBar count={corners} doneLabel="Find the letterboxes" onUndo={() => drawRef.current?.undo()} onCancel={() => drawRef.current?.cancel()} onDone={() => drawRef.current?.finish()} />}
              </div>
              {!drawing && (
                <p className="text-muted new-drop-hint new-drop-area-hint">
                  {inArea
                    ? <>Not quite right? <button type="button" className="nd-link" onClick={startDraw}>Draw it again</button></>
                    : <>Missing a street? <button type="button" className="nd-link" onClick={startDraw}>Draw the area instead</button> — every letterbox inside your outline comes in, however many.</>}
                </p>
              )}
              {!inArea && preview.total_in_radius > preview.count && <p className="text-muted new-drop-hint">{preview.total_in_radius - preview.count} more inside that radius were left out to keep to the number — move the slider to take them.</p>}
              {inArea && preview.total_in_area > preview.count && <p className="text-muted new-drop-hint">{preview.total_in_area - preview.count} more inside the outline were left out — a drop is capped at {preview.count}.</p>}
            </>
          ) : (
            <div className="new-drop-map placeholder"><span className="text-muted">The letterboxes appear here once they are looked up.</span></div>
          )}
        </div>
      </div>
      <div className="modal-actions">
        <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={create} disabled={!preview || preview.count === 0 || !!busy || drawing}>
          {busy === 'create' ? 'Creating…' : preview ? `Create drop (${preview.count})` : 'Create drop'}
        </button>
      </div>
    </Modal>
  );
}
